import { useEffect, useState } from "react";

import { Polyline } from "@web-app/components";
import { GoogleAddress } from "@web-app/types";

export interface DirectionsPolylineProps {
  directionsService: google.maps.DirectionsService | null;
  dropoffLocation: GoogleAddress | null;
  pickupLocation: GoogleAddress | null;
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWeight?: number;
}

/**
 * Component that fetches route from Google Directions API and renders it as a polyline
 */
export function DirectionsPolyline({
  pickupLocation,
  dropoffLocation,
  directionsService,
  strokeColor = "#1F2937",
  strokeOpacity = 0.8,
  strokeWeight = 3,
}: DirectionsPolylineProps) {
  const [routePath, setRoutePath] = useState<{ lat: number; lng: number }[]>(
    [],
  );

  useEffect(() => {
    if (
      !directionsService ||
      !pickupLocation?.geometry?.location ||
      !dropoffLocation?.geometry?.location
    ) {
      setRoutePath([]);
      return;
    }

    const origin = new google.maps.LatLng(
      pickupLocation.geometry.location.lat,
      pickupLocation.geometry.location.lng,
    );
    const destination = new google.maps.LatLng(
      dropoffLocation.geometry.location.lat,
      dropoffLocation.geometry.location.lng,
    );

    directionsService.route(
      {
        destination,
        origin,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && result) {
          const route = result.routes[0];
          if (route && route.overview_path) {
            const path = route.overview_path.map((point) => ({
              lat: point.lat(),
              lng: point.lng(),
            }));
            setRoutePath(path);
          }
        } else {
          console.error("Directions request failed:", status);
          setRoutePath([]);
        }
      },
    );
  }, [directionsService, pickupLocation, dropoffLocation]);

  if (routePath.length === 0) {
    return null;
  }

  return (
    <Polyline
      path={routePath}
      strokeColor={strokeColor}
      strokeOpacity={strokeOpacity}
      strokeWeight={strokeWeight}
      geodesic={true}
    />
  );
}
